import { useEffect, useState, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  styled,
  TooltipProps,
  Theme,
  tooltipClasses,
  Button,
  IconButton,
} from '@mui/material';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import { ReactComponent as DocSummary } from '../../../assests/svg/eca-summary.svg';
import { ReactComponent as LockIcon } from '../../../assests/svg/lock-icon.svg';
import { ReactComponent as SummaryIcon } from '../../../assests/svg/summary.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  setEcaDocZoom,
  setUpdateAllGridDocuments,
} from '../../../redux/slices/ecaDocumentViewerSlice';
import { RootState } from '../../../redux/store';
import EcaDocumentSummary from './EcaDocumentSummary';
import { t } from 'i18next';
import { fetchSummary, GenerateSummary } from '../../../api/EcaServices';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurButton from '../../common/aur-button/AurButton';
import { setToggleOnHighlight } from '../../../redux/slices/ecaDocumentHighLightPanelSlice';
import AurToast from '../../common/aur-toast/AurToast';
import {
  DELAY_MS,
  MAX_ATTEMPTS,
  SSE_ENDPOINT_ECA,
  SSE_SERVICE_TYPE,
  SUMMARY_FILE_TYPE,
} from '../../../constants/constants';
import { useToast } from '../../../contexts/toastContext/ToastContext';
import { AxiosError } from 'axios';
import { extractedNativeDownloadTextFile } from '../../../api/EcaManagementService';
import { colorArray } from '../../../constants/constants';
import { waitBeforeRetry } from '../../../utils/utils';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

const HIGHLIGHT_SELECTOR =
  '.doc-highlight-container>[class*="highlight-"],.doc-highlight-container>em';

const LightTooltip = styled(({ className, ...props }: TooltipProps) =>
  props?.title ? (
    <Tooltip
      {...props}
      classes={{ popper: className, arrow: 'custom-arrow' }}
      placement="top"
      arrow
    />
  ) : null
)(({ theme }: { theme: Theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    marginTop: -11,
  },
}));

const EcaDocumentToolBar = () => {
  const dispatch = useDispatch();
  const [zoom, setZoom] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentResult, setCurrentResult] = useState(-1);
  const [totalResults, setTotalResults] = useState(0);
  const [matches, setMatches] = useState<number[]>([]);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isPreviousDisabled, setIsPreviousDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const { toggleOnHighlight: highlightToggleOn } = useSelector(
    (state: RootState) => state.ecaHighLightText
  );
  const { extractedTextPath, nativePath: nativeTextPath, activeDocsFlag: activeDocsFileName, ecaDocZoom: currentZoom } = useSelector(
    (state: RootState) => state.ecaDocumentViewer
  );

  const { hasDocumentSummaryGenerationPerm } = usePermissionsByPage(
    PAGE_NAME.ECA_DOC_TOOL_BAR
  );

  const [showButtonLoader, setShowButtonLoader] = useState<boolean>(false);
  const [summaryGeneratedSuccess, setSummaryGeneratedSuccess] =
    useState<boolean>(false);
  const [summaryContent, setSummaryContent] = useState('');
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [showHiddenSummary, setShowHiddenSummary] = useState<boolean>(false);
  const [nlpNotEnabled, setNlpNotEnabled] = useState<boolean>(false);
  const [openToast] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [summariesNotEnabled, setSummariesNotEnabled] =
    useState<boolean>(false);
  const [createSummaryForSpecificType, setCreateSummaryForSpecificType] =
    useState<boolean>(false);
  const { clientId } = useSelector(
    (state: RootState) => state.selectedClient
  );

  const { projectId, nlpSearchEnabled } = useSelector(
    (state: RootState) => state.reports
  );
  const { fullScreenView: fullScreen, documentId, documentContent, documentLoading, allGridDocuments } = useSelector(
    (state: RootState) => state.ecaDocumentViewer
  );

  const [highlightedText, setHighlightedText] = useState(documentContent);

  const { showToast } = useToast();

  const searchText = (term: string) => {
    if (!term) {
      setTotalResults(0);
      setHighlightedText(documentContent);
      setMatches([]);
      setCurrentResult(-1);
      return;
    }

    setSearchLoading(true);
    const formattedTerm = term.replace('<', '&lt;').replace('>', '&gt;'); //To match server formatting

    const regex = new RegExp(`(${formattedTerm})`, 'gi');
    const matchesArray = [...documentContent.matchAll(regex)]; // Get all matches
    setMatches(matchesArray.map((match) => match.index!));
    if (!matchesArray.length) {
      // No matches found, reset text
      setTotalResults(0);
      setCurrentResult(0);
      setSearchLoading(false);
      return;
    }

    const highlighted = documentContent.replace(regex, (match) => {
      return `<span class="search" style="background-color: #D9E277">${match}</span>`;
    });

    setHighlightedText(highlighted); // Set highlighted text
    setTotalResults(matchesArray.length);
    setCurrentResult(1); // Start from the first match
    setSearchLoading(false);

    setTimeout(() => {
      const firstHighlight =
        contentRef.current?.querySelectorAll('.highlight')[0];
      if (firstHighlight) {
        firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  // Function to handle search term input with debounce
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    if (searchTerm === '') {
      setCurrentResult(-1);
    }
  }, [searchText]);

  useEffect(() => {
    updateTermStyle();
    setSearchLoading(false);
  }, [highlightedText]);

  useEffect(() => {
    setHighlightedText(documentContent);
    setSearchLoading(false);
  }, [documentContent]);

  useEffect(() => {
    if (documentId !== '' && Array.isArray(allGridDocuments)) {
      const selectedDoc = allGridDocuments.find(
        (doc) => doc.documentid === documentId
      );
      setSummaryGeneratedSuccess(false);
      setShowHiddenSummary(selectedDoc?.nlp_summary_text ? true : false);
      setCreateSummaryForSpecificType(
        SUMMARY_FILE_TYPE.includes(selectedDoc?.file_type?.toLowerCase() ?? '')
      );

      setShowSummary(false);
      setSummaryContent(selectedDoc?.nlp_summary_text ?? '');
      if (!hasDocumentSummaryGenerationPerm || !nlpSearchEnabled) {
        setNlpNotEnabled(!!hasDocumentSummaryGenerationPerm);
      }
    }
  }, [
    documentId,
    allGridDocuments,
    hasDocumentSummaryGenerationPerm,
    nlpSearchEnabled,
  ]);

  // Function to handle search term input with "Enter" key press
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      searchText(searchTerm);
    }
  };

  // Function to handle search icon click
  const handleSearchClick = () => {
    searchText(searchTerm);
  };
  // Function to handle generate summary
  const handleGenerateSummary = () => {
    hasDocumentSummaryGenerationPerm && getGenerateSummary();
  };
  // Function to fetch generated summary
  const handleAvailableSummary = () => {
    fetchGeneratedSummary();
  };
  const handleHideButtonClicked = () => {
    setShowSummary(false);
    setSummaryGeneratedSuccess(false);
    setShowHiddenSummary(true);
  };
  const handleHideNlpButtonClicked = () => {
    setSummariesNotEnabled(false);
  };
  const handleShowHiddenSummary = () => {
    setShowSummary(true);
  };
  const handleNlpnotEnabledSummary = () => {
    setSummariesNotEnabled(true);
  };

  // Function to navigate to the next search result
  const nextResult = () => {
    if (currentResult < totalResults) {
      setCurrentResult((prev) => prev + 1);
    }
  };

  // Function to navigate to the previous search result
  const prevResult = () => {
    if (currentResult > 1) {
      setCurrentResult((prev) => prev - 1);
    }
  };

  // function to call the Api for GenerateSummary
  const getGenerateSummary = async () => {
    try {
      setShowButtonLoader(true);
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await GenerateSummary(params, documentId);
      if (result.status === 200) {
        setRequestId(result?.data?.data?.requestId);
      } else {
        setSummaryGeneratedSuccess(false);
        setShowButtonLoader(false);
        showToast(
          t('ecaDocList.errorGenerateSummary'),
          'error',
          undefined,
          'center'
        );
      }
    } catch (e) {
      setShowButtonLoader(false);
      setSummaryGeneratedSuccess(false);
      const err = e as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
    }
  };

  // function to fetch the API for Generated Summary with retry
  const fetchGeneratedSummary = async (attempt = 1): Promise<void> => {
    try {
      setShowButtonLoader(true);
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await fetchSummary(params, documentId);
      const summary = result?.data?.data?.documentSummary;
      setSummaryContent(summary);
      if (result.status === 200 && summary) {
        setSummaryGeneratedSuccess(true);
        setShowButtonLoader(false);
        setShowSummary(true);

        const updateAllGridDoc = allGridDocuments.map((row: any) =>
          row.documentid === documentId
            ? { ...row, nlp_summary_text: summary }
            : row
        );
        dispatch(setUpdateAllGridDocuments(updateAllGridDoc));
      } else {
        if (attempt < MAX_ATTEMPTS) {
          await waitBeforeRetry(DELAY_MS);
          await fetchGeneratedSummary(attempt + 1);
        } else {
          showToast(
            t('ecaDocList.errorGenerateSummary'),
            'error',
            undefined,
            'center'
          );
          setSummaryGeneratedSuccess(false);
          setShowButtonLoader(false);
        }
      }
    } catch (e) {
      if (attempt < MAX_ATTEMPTS) {
        await waitBeforeRetry(DELAY_MS);
        await fetchGeneratedSummary(attempt + 1);
      } else {
        setSummaryGeneratedSuccess(false);
        setShowButtonLoader(false);
        const err = e as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
      }
    }
  };
  // Function to scroll to the current match
  const scrollToMatch = (index: number) => {
    const matchIndex = matches[index];
    if (matchIndex !== undefined) {
      const elements = document.querySelectorAll('.search');
      elements.forEach((element) => {
        (element as HTMLElement).style.fontWeight = 'normal';
      });

      const matchElement = elements.item(index) as HTMLElement;

      if (matchElement) {
        matchElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        matchElement.style.backgroundColor = '#D9E277';
        matchElement.style.fontWeight = 'bold';
      }
    }
  };

  useEffect(() => {
    if (requestId !== '') {
      const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.SEARCH_SERVICE}`;
      const eventSource = new EventSource(SSE_URL);
      eventSource.onmessage = (event) => {
        const responseData = JSON.parse(event.data);
        if (responseData.status.toLowerCase() === 'completed') {
          setShowButtonLoader(false);
          fetchGeneratedSummary();
          eventSource.close();
        } else {
          showToast(
            t('ecaDocList.errorGenerateSummary'),
            'error',
            undefined,
            'center'
          );
          setShowButtonLoader(false);
          setSummaryGeneratedSuccess(false);
          eventSource.close();
        }
      };
      eventSource.onerror = (_error) => {
        showToast(
          t('ecaDocList.errorGenerateSummary'),
          'error',
          undefined,
          'center'
        );
        eventSource.close();
        setShowButtonLoader(false);
        setSummaryGeneratedSuccess(false);
      };
    }
  }, [requestId]);

  useEffect(() => {
    // Call scrollToMatch when currentResult changes
    if (matches.length > 0 && currentResult > 0) {
      scrollToMatch(currentResult - 1); // Index in matches is zero-based
    }
  }, [currentResult, matches]);


  useEffect(() => {
    if (currentZoom && currentZoom !== zoom) {
      setZoom(currentZoom);
    }
  }, [currentZoom]);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + 10, 150);
    setZoom(newZoom);
    dispatch(setEcaDocZoom(newZoom));
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - 10, 50);
    setZoom(newZoom);
    dispatch(setEcaDocZoom(newZoom));
  };

  // Styling for the highlighted words
  const highlightStyle = `
    em {
      background-color: #D9E277;
      font-style:normal
    }
  `;
  const highlightStyleNormal = `
  em {
    font-style:normal
  }
`;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDownloadExtractTextFile = async (filePath: string): Promise<void> => {
    setAnchorEl(null);
    if (!filePath) {
      console.warn('File path is empty. Skipping download.');
      return;
    }

    try {
      const response = await extractedNativeDownloadTextFile(filePath);

      // Determine the file extension
      const extensionMatch = filePath.match(/\.(\w+)$/);
      const fileExtension = extensionMatch ? extensionMatch[1] : 'txt'; // default to txt

      // Extract filename from path or generate one
      const filename =
        filePath.split('\\').pop() || `${activeDocsFileName}.${fileExtension}`;

      // Create blob and trigger browser download
      const blob = new Blob([response.data]);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading extracted text file:', error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const toggleOnHandlers = () => {
    dispatch(setToggleOnHighlight(false));
  };

  const toggleOFFHandlers = () => {
    dispatch(setToggleOnHighlight(true));
  };
  const scrollToHighlight = (index: number) => {
    if (!contentRef.current) return;

    const highlightedElements = Array.from(
      contentRef.current.querySelectorAll(HIGHLIGHT_SELECTOR)
    ).filter((el) => el.textContent?.trim());

    // Reset all to normal fontWeight

    highlightedElements.forEach((el) => {
      (el as HTMLElement).style.fontWeight = 'normal';
    });

    const target = highlightedElements[index] as HTMLElement;

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      target.style.fontWeight = 'bold';
    }
  };

  useEffect(() => {
    setHighlightIndex(-1);
    setCurrentResult(-1);
  }, [documentContent]);

  const updateTermStyle = () => {
    const terms =
      contentRef.current?.querySelectorAll(HIGHLIGHT_SELECTOR) || [];
    terms.forEach((term) => {
      const termElement = term as HTMLElement;
      const termColor = Array.from(term.classList)
        .filter((termClass) => termClass.includes('highlight-'))
        .map((termClass) => termClass.replace('highlight-', '#').toUpperCase());
      termElement.style.fontWeight = 'normal';
      termElement.style.color = highlightToggleOn
        ? colorArray.find((item) => item.backgroundColor === termColor[0])
          ?.textColor || '#000000'
        : '#000000';
    });
  };

  useEffect(() => {
    if (!highlightToggleOn || !contentRef.current) {
      setIsPreviousDisabled(true);
      setIsNextDisabled(true);
      updateTermStyle();
      return;
    }

    const highlightedElements =
      contentRef.current.querySelectorAll(HIGHLIGHT_SELECTOR) || [];

    if (highlightedElements.length === 0) {
      setIsPreviousDisabled(true);
      setIsNextDisabled(true);
      return;
    }

    const totalHighlights = highlightedElements.length;

    // Enable/disable chevrons
    setIsPreviousDisabled(highlightIndex <= 0);
    setIsNextDisabled(highlightIndex >= totalHighlights - 1);

    updateTermStyle();
    // Apply bold to current highlight
    highlightedElements.forEach((el, index) => {
      const element = el as HTMLElement;
      element.style.fontWeight = index === highlightIndex ? 'bold' : 'normal';
    });
  }, [highlightIndex, highlightToggleOn, contentRef.current]);

  const goToNextHighlight = () => {
    const totalHighlights =
      contentRef.current?.querySelectorAll(HIGHLIGHT_SELECTOR).length || 0;

    const nextIndex = highlightIndex + 1;

    if (nextIndex < totalHighlights) {
      setHighlightIndex(nextIndex);
      scrollToHighlight(nextIndex);
    }
  };

  const goToPrevHighlight = () => {
    const prevIndex = highlightIndex - 1;

    if (prevIndex >= 0) {
      setHighlightIndex(prevIndex);
      scrollToHighlight(prevIndex);
    }
  };

  const getSummaryText = (
    summaryGeneratedSuccess: boolean,
    showHiddenSummary: boolean,
    nlpNotEnabled: boolean
  ): string => {
    switch (true) {
      case summaryGeneratedSuccess:
        return 'Summary Available';
      case showHiddenSummary:
        return 'Show Summary';
      case nlpNotEnabled:
        return 'Generate Summary';
      default:
        return 'Generate Summary';
    }
  };
  const handleSummaryAction = (
    summaryGeneratedSuccess: boolean,
    showHiddenSummary: boolean,
    handleAvailableSummary: () => void,
    handleGenerateSummary: () => void
  ) => {
    switch (true) {
      case summaryGeneratedSuccess:
        handleAvailableSummary(); // Call function to handle available summary
        break;

      case showHiddenSummary:
        handleShowHiddenSummary();
        break;
      case nlpNotEnabled:
        handleNlpnotEnabledSummary();
        break;

      default:
        handleGenerateSummary(); // Call function to generate summary
        break;
    }
  };
  const getStartIcon = (
    showButtonLoader: boolean,
    showHiddenSummary: boolean,
    nlpNotEnabled: boolean,
    summaryGeneratedSuccess: boolean
  ) => {
    switch (true) {
      case showButtonLoader:
        return <AurLoadingIndicator size={18} className="progress-icon" />;
      case showHiddenSummary:
        return <SummaryIcon aria-hidden="true" />;
      case summaryGeneratedSuccess:
        return <SummaryIcon aria-hidden="true" />;
      case nlpNotEnabled:
        return <LockIcon aria-hidden="true" />;
      default:
        return <DocSummary aria-hidden="true" />;
    }
  };

  const renderGenerateSummary = () => {
    if (summaryContent) {
      return (
        <Box className="view-settings-summary-container">
          {showSummary ? (
            <Box className="summary-container">
              <Box className="text-section">
                <div className="heading-subheading-summary">
                  <div>
                    <Typography className="typo-text subhead2_size">
                      Summary:
                    </Typography>

                    <Typography className="typo-text body3_size menu-list">
                      {summaryContent}
                    </Typography>
                  </div>
                  <AurButton
                    classNames="hide-button-summary"
                    type="text"
                    size="Small"
                    onClick={handleHideButtonClicked}
                  >
                    Hide
                  </AurButton>
                </div>
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={getStartIcon(false, showHiddenSummary, false, false)}
              onClick={() =>
                handleSummaryAction(
                  false,
                  showHiddenSummary,
                  handleAvailableSummary,
                  handleGenerateSummary
                )
              }
              className="generate-button"
            >
              <span
                className={`${showHiddenSummary ? 'summary-active-label' : 'summary-inactive-label'} summary-label `}
              >
                {getSummaryText(false, showHiddenSummary, false)}{' '}
              </span>
            </Button>
          )}
        </Box>
      );
    }

    if (hasDocumentSummaryGenerationPerm && createSummaryForSpecificType) {
      return (
        <Box className="view-settings-summary-container">
          {!showSummary && !summariesNotEnabled ? (
            <Button
              variant="contained"
              startIcon={getStartIcon(
                showButtonLoader,
                showHiddenSummary,
                nlpNotEnabled,
                summaryGeneratedSuccess
              )}
              onClick={() =>
                handleSummaryAction(
                  summaryGeneratedSuccess,
                  showHiddenSummary,
                  handleAvailableSummary,
                  handleGenerateSummary
                )
              }
              className="generate-button"
            >
              <span
                className={`${showHiddenSummary ? 'summary-active-label' : 'summary-inactive-label'} summary-label `}
              >
                {getSummaryText(
                  summaryGeneratedSuccess,
                  showHiddenSummary,
                  nlpNotEnabled
                )}{' '}
              </span>
            </Button>
          ) : null}
          {showSummary && (
            <Box className="summary-container">
              <Box className="text-section">
                <div className="heading-subheading-summary">
                  <div>
                    <Typography className="typo-text subhead2_size">
                      Summary:
                    </Typography>

                    <Typography className="typo-text body3_size menu-list">
                      {summaryContent}
                    </Typography>
                  </div>
                  <AurButton
                    classNames="hide-button-summary"
                    type="text"
                    size="Small"
                    onClick={handleHideButtonClicked}
                  >
                    Hide
                  </AurButton>
                </div>
              </Box>
            </Box>
          )}
          {summariesNotEnabled && (
            <Box className="summary-container">
              <Box className="text-section">
                <div className="heading-subheading-summary">
                  <div>
                    <Typography className="typo-text subhead2_size">
                      {t('ecaDocList.nlpNotEnabledHeader')}
                    </Typography>

                    <Typography className="typo-text body3_size menu-list">
                      {t('ecaDocList.nlpNotEnabledDesc')}
                    </Typography>
                  </div>
                  <AurButton
                    classNames="hide-button-summary"
                    type="text"
                    size="Small"
                    onClick={handleHideNlpButtonClicked}
                  >
                    Hide
                  </AurButton>
                </div>
              </Box>
            </Box>
          )}
        </Box>
      );
    }
  };

  return (
    <Box>
      <Box
        className="toolbar-container"
        justifyContent="space-between"
        flexDirection="row"
      >
        {/* Zoom Controls */}
        <Box className="zoom-out">
          {zoom > 50 ? (
            <IconButton
              onClick={handleZoomOut}
              className="icon-hover"
              aria-label={t('toolbar.zoomOut')}
              tabIndex={0}
            >
              <AurSvgIcon src="images/eca-doc-zoom-out-icon.svg" />
            </IconButton>
          ) : (
            <div className="icon-hover">
              <AurSvgIcon src="images/eca-doc-zoom-minus-disabled-icon.svg" />
            </div>
          )}
          <TextField
            value={`${zoom}%`}
            className="zoom-input"
            InputProps={{
              readOnly: true,
              'aria-hidden': true,
              'aria-label': t('toolbar.zoomPercentage'),
              tabIndex: -1,
            }}
            aria-hidden
          />
          {zoom >= 150 ? (
            <div className="icon-hover">
              <AurSvgIcon src="images/eca-doc-zoom-plus-disabled-icon.svg" />
            </div>
          ) : (
            <IconButton
              onClick={handleZoomIn}
              className="icon-hover"
              aria-label={t('toolbar.zoomIn')}
              tabIndex={0}
            >
              <AurSvgIcon src="images/eca-doc-zoom-in-icon.svg" />
            </IconButton>
          )}
          <div className="vertical-line">
            <AurSvgIcon src="images/eca-vertical-line-icon.svg" />
          </div>
          <IconButton
            onClick={!isPreviousDisabled ? goToPrevHighlight : undefined}
            className="icon-hover"
            aria-label={t('toolbar.previousHighlight')}
            tabIndex={isPreviousDisabled ? -1 : 0}
          >
            <AurSvgIcon
              src={
                isPreviousDisabled
                  ? 'images/eca-doc-chevron-left-icon.svg'
                  : 'images/eca-doc-chevron-left-icon-active.svg'
              }
            />
          </IconButton>

          {highlightToggleOn ? (
            <LightTooltip
              title={
                <div className="typo-text label3_size on-off-tooltip">
                  {'Turn Highlights Off'}
                </div>
              }
            >
              <IconButton
                onClick={() => toggleOnHandlers()}
                className="icon-hover"
                aria-label={t('toolbar.turnHighlightsOff')}
                tabIndex={0}
              >
                <AurSvgIcon src="images/eca-doc-highlight-icon.svg" />
              </IconButton>
            </LightTooltip>
          ) : (
            <LightTooltip
              title={
                <div className="typo-text label3_size on-off-tooltip">
                  {t('toolbar.turnHighlightsOn')}
                </div>
              }
            >
              <IconButton
                onClick={() => toggleOFFHandlers()}
                className="icon-hover"
                aria-label={t('toolbar.turnHighlightsOn')}
                tabIndex={0}
              >
                <AurSvgIcon src="images/eca-doc-highlight-toggle-off-icon.svg" />
              </IconButton>
            </LightTooltip>
          )}
          <IconButton
            onClick={!isNextDisabled ? goToNextHighlight : undefined}
            className="icon-hover"
            aria-label={t('toolbar.nextHighlight')}
            tabIndex={isNextDisabled ? -1 : 0}
          >
            <AurSvgIcon
              src={
                isNextDisabled
                  ? 'images/eca-doc-highlight-right-disabled-chevron.svg'
                  : 'images/eca-doc-chevron-right-icon.svg'
              }
            />
          </IconButton>
        </Box>

        <Box className="zoom-out">
          <div
            className={
              currentResult !== -1
                ? 'search-section-container'
                : 'search-section-container-zero'
            }
          >
            <TextField
              className="search-section"
              placeholder="Search Document"
              value={searchTerm}
              tabIndex={0}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& input::placeholder': {
                    color: '#0C1119 !important',
                    opacity: 1,
                  },
                },
              }}
              onChange={handleSearchInput} // Just update the input value
              onKeyDown={handleKeyDown} // Trigger search on Enter key
            />
            {currentResult !== -1 ? (
              <Typography className="typo-text body4_size current-result">
                {currentResult}/{totalResults}
              </Typography>
            ) : null}
            <span className="previous-result">
              {/* Previous Icon: Disabled when currentResult is 1 */}

              {currentResult === 0 || currentResult === 1 ? (
                <button className="icon-hover disabled">
                  <AurSvgIcon src="images/eca-doc-search-previous-icon.svg" />
                </button>
              ) : currentResult !== -1 ? (
                <button
                  onClick={prevResult}
                  className="icon-hover"
                  aria-label={t('toolbar.showPreviousResult')}
                  tabIndex={0}
                >
                  <AurSvgIcon src="images/eca-doc-search-previous-enabled-icon.svg" />
                </button>
              ) : null}

              {/* Next Icon: Disabled when currentResult equals totalResults */}
              {currentResult === totalResults ? (
                <button className="icon-hover disabled next-icon">
                  <AurSvgIcon src="images/eca-doc-search-next-icon-disabled.svg" />
                </button>
              ) : currentResult !== -1 ? (
                <button
                  onClick={nextResult}
                  className="icon-hover next-icon"
                  aria-label={t('toolbar.showNextResult')}
                  tabIndex={0}
                >
                  <AurSvgIcon src="images/eca-doc-search-next-icon.svg" />
                </button>
              ) : null}

              <span className="icon-hover" onClick={handleSearchClick}>
                <AurSvgIcon src="images/eca-doc-search-icon.svg" />
              </span>
            </span>
          </div>

          <IconButton
            className="download-summary-icon"
            onClick={(event: any) => handleClick(event)}
            aria-label={t('toolbar.downloadDocument')}
            tabIndex={0}
          >
            <AurSvgIcon src="images/eca-doc-download-icon.svg" />
          </IconButton>

          <div>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{ 'aria-labelledby': 'basic-button' }}
              disableScrollLock
              className={
                fullScreen ? 'download-menu-fullscreen' : 'download-menu'
              }
            >
              <Typography className="typo-text body4_size menu-title">
                {t('ecaDocList.downloadAs')}
              </Typography>
              <MenuItem
                // disabled={!extractedTextPath}
                onClick={() =>
                  onDownloadExtractTextFile(extractedTextPath as string)
                }
              >
                <Typography className="typo-text body3_size menu-list menu-content">
                  {t('ecaDocList.extractedText')}
                </Typography>
              </MenuItem>
              <MenuItem
                // disabled={!nativeTextPath}
                onClick={() =>
                  onDownloadExtractTextFile(nativeTextPath as string)
                }
              >
                <Typography className="typo-text body3_size menu-list menu-content">
                  {' '}
                  {t('ecaDocList.native')}
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        </Box>
      </Box>
      {renderGenerateSummary()}
      <EcaDocumentSummary />

      <div className="docs">
        <style>
          {highlightToggleOn ? highlightStyle : highlightStyleNormal}
        </style>
        {documentLoading ? (
          <AurLoadingIndicator size={50} isOpen={documentLoading} />
        ) : searchLoading ? (
          <AurLoadingIndicator size={50} isOpen={searchLoading} />
        ) : (
          <div
            tabIndex={0}
            ref={(node) => (contentRef.current = node)}
            dangerouslySetInnerHTML={{
              __html: highlightedText,
            }}
            className={
              highlightToggleOn
                ? 'doc-highlight-container'
                : 'doc-highlight-container highlights-off'
            }
            style={{
              fontSize: `${zoom}%`,
            }}
          />
        )}
      </div>
      <div className="aur-toast-download">
        <AurToast
          hideDuration={5000}
          open={openToast}
          severity={'error'}
          message="Failed to create summary."
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onClose={() => { }}
          horizontalPosition={'center'}
        />
      </div>
    </Box>
  );
};

export default EcaDocumentToolBar;
